/**
 * Preset lists carried over verbatim from the previous editor (photo-editor.component.tsx, 2026-09),
 * plus a YouTube group. Percent and by-height presets drive Resize; aspect ratios drive Crop;
 * platform sizes are offered by both (Resize fits inside the box, Crop takes the aspect and the
 * exact pixels — see model/edit-stack.ts setResizeBox / setCropToPlatformSize).
 */
import type { Size } from './model/edit-stack';

export const PERCENT_PRESETS = [25, 50, 75, 150, 200] as const;

export const HEIGHT_PRESETS = [170, 200, 315, 320, 396, 400, 500, 566, 627, 628, 851, 900, 1080, 1200, 1350, 1500, 1584, 1600, 1920] as const;

export interface AspectPreset { label: string; value: number | 'original' | null; }
export const ASPECT_PRESETS: AspectPreset[] = [
    { label: 'Free', value: null },
    { label: 'Original', value: 'original' },
    { label: '1:1', value: 1 },
    { label: '4:3', value: 4 / 3 },
    { label: '3:2', value: 3 / 2 },
    { label: '16:9', value: 16 / 9 },
    { label: '16:10', value: 16 / 10 },
    { label: '2:1', value: 2 },
];

export interface PlatformSize extends Size { label: string; }
export interface PlatformGroup { name: string; sizes: PlatformSize[]; }

export const PLATFORM_GROUPS: PlatformGroup[] = [
    { name: 'Facebook', sizes: [
        { label: 'Profile photo', width: 170, height: 170 }, { label: 'Landscape', width: 1200, height: 628 },
        { label: 'Portrait', width: 628, height: 1200 }, { label: 'Square', width: 1200, height: 1200 },
        { label: 'Stories', width: 1080, height: 1920 }, { label: 'Cover photo', width: 851, height: 315 } ] },
    { name: 'Instagram', sizes: [
        { label: 'Profile photo', width: 320, height: 320 }, { label: 'Landscape', width: 1080, height: 566 },
        { label: 'Portrait', width: 1080, height: 1350 }, { label: 'Square', width: 1080, height: 1080 },
        { label: 'Stories', width: 1080, height: 1920 } ] },
    { name: 'X / Twitter', sizes: [
        { label: 'Profile photo', width: 400, height: 400 }, { label: 'Landscape', width: 1600, height: 900 },
        { label: 'Portrait', width: 1080, height: 1350 }, { label: 'Square', width: 1080, height: 1080 },
        { label: 'Cover photo', width: 1500, height: 500 } ] },
    { name: 'LinkedIn', sizes: [
        { label: 'Profile photo', width: 400, height: 400 }, { label: 'Landscape', width: 1200, height: 627 },
        { label: 'Portrait', width: 627, height: 1200 }, { label: 'Square', width: 1080, height: 1080 },
        { label: 'Cover photo', width: 1584, height: 396 } ] },
    { name: 'TikTok', sizes: [
        { label: 'Profile photo', width: 200, height: 200 }, { label: 'Landscape', width: 1920, height: 1080 },
        { label: 'Portrait', width: 1080, height: 1920 }, { label: 'Square', width: 1080, height: 1080 } ] },
    { name: 'YouTube', sizes: [
        { label: 'Thumbnail', width: 1280, height: 720 }, { label: 'Channel banner', width: 2560, height: 1440 },
        { label: 'Profile photo', width: 800, height: 800 } ] },
];

export function platformSizeLabel(g: PlatformGroup, s: PlatformSize): string {
    return `${s.width} × ${s.height} · ${s.label}`;
}
